export type AnalyticsAttribution = {
	utm_source?: string
	utm_medium?: string
	utm_campaign?: string
	utm_content?: string
	utm_term?: string
	landing_path: string
	referrer_host?: string
	captured_at: string
}

type StoredAttribution = AnalyticsAttribution & { expires_at: string }

const firstTouchStorageKey = 'analytics_first_touch_v1'
const sessionTouchStorageKey = 'analytics_session_touch_v1'
const firstTouchTtlMs = 30 * 24 * 60 * 60 * 1000
const utmKeys = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term',
] as const

function normalizeCampaignValue(value: string | null): string | undefined {
	if (!value) return undefined
	const normalized = value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9_-]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, 100)
	return normalized || undefined
}

function getReferrerHost(referrer: string): string | undefined {
	if (!referrer) return undefined
	try {
		return new URL(referrer).hostname.toLowerCase()
	} catch {
		return undefined
	}
}

function readStoredAttribution(
	storage: Storage,
	key: string,
): AnalyticsAttribution | null {
	const raw = storage.getItem(key)
	if (!raw) return null
	try {
		const parsed = JSON.parse(raw) as StoredAttribution
		if (Date.parse(parsed.expires_at) <= Date.now()) {
			storage.removeItem(key)
			return null
		}
		const { expires_at: _expiresAt, ...attribution } = parsed
		return attribution
	} catch {
		storage.removeItem(key)
		return null
	}
}

function writeStoredAttribution(
	storage: Storage,
	key: string,
	attribution: AnalyticsAttribution,
) {
	storage.setItem(
		key,
		JSON.stringify({
			...attribution,
			expires_at: new Date(Date.now() + firstTouchTtlMs).toISOString(),
		}),
	)
}

export function captureAnalyticsAttribution(): void {
	if (typeof window === 'undefined') return

	const searchParams = new URLSearchParams(window.location.search)
	const campaign = Object.fromEntries(
		utmKeys.flatMap((key) => {
			const value = normalizeCampaignValue(searchParams.get(key))
			return value ? [[key, value]] : []
		}),
	) as Partial<Pick<AnalyticsAttribution, (typeof utmKeys)[number]>>
	const hasCampaign = Object.keys(campaign).length > 0
	const existingFirstTouch = readStoredAttribution(
		window.localStorage,
		firstTouchStorageKey,
	)

	if (!hasCampaign && existingFirstTouch) return

	const referrerHost = getReferrerHost(document.referrer)
	const attribution: AnalyticsAttribution = {
		...campaign,
		landing_path: window.location.pathname,
		...(referrerHost ? { referrer_host: referrerHost } : {}),
		captured_at: new Date().toISOString(),
	}

	if (!existingFirstTouch) {
		writeStoredAttribution(
			window.localStorage,
			firstTouchStorageKey,
			attribution,
		)
	}
	if (
		hasCampaign ||
		!readStoredAttribution(window.sessionStorage, sessionTouchStorageKey)
	) {
		writeStoredAttribution(
			window.sessionStorage,
			sessionTouchStorageKey,
			attribution,
		)
	}
}

export function getAnalyticsAttributionProperties(): Record<string, string> {
	if (typeof window === 'undefined') return {}
	const firstTouch = readStoredAttribution(
		window.localStorage,
		firstTouchStorageKey,
	)
	const sessionTouch = readStoredAttribution(
		window.sessionStorage,
		sessionTouchStorageKey,
	)
	const properties: Record<string, string> = {}

	for (const key of utmKeys) {
		const currentValue = sessionTouch?.[key]
		const firstValue = firstTouch?.[key]
		if (currentValue) properties[key] = currentValue
		if (firstValue) properties[`first_${key}`] = firstValue
	}
	if (sessionTouch?.landing_path) {
		properties.landing_path = sessionTouch.landing_path
	}
	if (firstTouch?.landing_path) {
		properties.first_landing_path = firstTouch.landing_path
	}
	if (sessionTouch?.referrer_host) {
		properties.referrer_host = sessionTouch.referrer_host
	}
	if (firstTouch?.referrer_host) {
		properties.first_referrer_host = firstTouch.referrer_host
	}

	return properties
}

/**
 * First-touch campaign for the signup form. It exists only when an analytics
 * event ran with consent, because capture happens inside those events; the
 * caller still checks consent before copying it into the account.
 */
export function getFirstTouchForSignup(): {
	utmSource?: string
	utmMedium?: string
	utmCampaign?: string
	utmContent?: string
	utmTerm?: string
	landingPath?: string
} | null {
	if (typeof window === 'undefined') return null
	const firstTouch = readStoredAttribution(
		window.localStorage,
		firstTouchStorageKey,
	)
	if (!firstTouch) return null
	return {
		utmSource: firstTouch.utm_source,
		utmMedium: firstTouch.utm_medium,
		utmCampaign: firstTouch.utm_campaign,
		utmContent: firstTouch.utm_content,
		utmTerm: firstTouch.utm_term,
		landingPath: firstTouch.landing_path,
	}
}
